import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { ClickableQrCode } from "@/components/ui/ClickableQrCode";
import { NativeSelectField } from "@/components/ui/NativeSelect";
import { Spinner } from "@/components/ui/Spinner";
import { TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  getMaterialAnforderungCreateQr,
  getMaterialAnforderungCreateQrPrintUrl,
  type MaCreateQrPaperSize,
  type MaCreateQrResponse,
} from "@/lib/material";
import { PrintJobFeedbackError } from "@/lib/printJob";
import {
  getDefaultPrintLocation,
  getPrintLocations,
  runPrintByLocationWithFeedback,
  setDefaultPrintLocation,
  type PrintLocation,
} from "@/lib/projects";
import { toast } from "@/lib/toast";
import { PROJECT_OVERVIEW_NESTED_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

export function MaterialRequestQrDialog({
  open,
  onClose,
  orderId,
}: {
  open: boolean;
  onClose: () => void;
  orderId: number;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MaCreateQrResponse | null>(null);
  const [locations, setLocations] = useState<PrintLocation[]>([]);
  const [locationId, setLocationId] = useState("");
  const [copies, setCopies] = useState("1");
  const [printBusy, setPrintBusy] = useState<MaCreateQrPaperSize | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setData(null);
      setError(null);
      setCopied(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const qr = await getMaterialAnforderungCreateQr(orderId);
        if (!cancelled) setData(qr);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : "QR-Code konnte nicht erzeugt werden.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, orderId]);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      try {
        const [locs, def] = await Promise.all([
          getPrintLocations(),
          getDefaultPrintLocation(),
        ]);
        setLocations(Array.isArray(locs) ? locs : []);
        if (def?.default_location_id) {
          setLocationId(String(def.default_location_id));
        } else if (locs?.[0]) {
          const first = locs[0];
          const id = first.id ?? first.key ?? first.value;
          if (id != null) setLocationId(String(id));
        }
      } catch {
        setLocations([]);
      }
    })();
  }, [open]);

  async function copyLink() {
    if (!data?.url) return;
    try {
      await navigator.clipboard.writeText(data.url);
      setCopied(true);
      toast.success("Link kopiert.");
    } catch {
      toast.error("Link konnte nicht kopiert werden.");
    }
  }

  async function printPoster(size: MaCreateQrPaperSize) {
    const loc = Number(locationId);
    const numCopies = Math.max(1, Number(copies) || 1);
    if (!loc) {
      toast.error("Bitte einen Druckstandort wählen.");
      return;
    }
    setPrintBusy(size);
    try {
      await setDefaultPrintLocation(loc);
      const { url } = await getMaterialAnforderungCreateQrPrintUrl(orderId, size);
      await runPrintByLocationWithFeedback(loc, numCopies, url, {
        loadingMessage: `DIN ${size} wird gedruckt…`,
      });
    } catch (err) {
      if (err instanceof PrintJobFeedbackError) {
        return;
      }
      toast.error(err instanceof Error ? err.message : "Druck fehlgeschlagen.");
    } finally {
      setPrintBusy(null);
    }
  }

  async function openPdf(size: MaCreateQrPaperSize) {
    setPrintBusy(size);
    try {
      const { url } = await getMaterialAnforderungCreateQrPrintUrl(orderId, size);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "PDF konnte nicht geöffnet werden.");
    } finally {
      setPrintBusy(null);
    }
  }

  const project = data?.auftrag;
  const meta = [
    project?.kunde ? `Kunde: ${project.kunde}` : null,
    project?.ort_line ? `Ort: ${project.ort_line}` : null,
    project?.beschreibung || null,
  ].filter(Boolean);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="QR-Code Materialanforderung"
      description="Aushang für die Baustelle: Nach dem Scannen öffnet sich die Materialanforderung mit diesem Projekt. Der Link bleibt gültig."
      size="md"
      zIndexClass={PROJECT_OVERVIEW_NESTED_OVERLAY_Z}
      plainBackdrop
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8">
            <Spinner />
            <span className="text-sm text-label-secondary">QR-Code wird erzeugt …</span>
          </div>
        ) : null}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        {data?.qr_data_uri ? (
          <div className="flex flex-col items-center gap-3 rounded-ios-lg border border-separator bg-fill-tertiary/60 px-4 py-5">
            <ClickableQrCode
              src={data.qr_data_uri}
              href={data.url}
              alt="QR-Code für neue Materialanforderung"
              className="h-44 w-44 rounded-ios border border-separator bg-white p-2 shadow-sm"
            />
            {project?.auftragsnummer ? (
              <p className="text-center text-sm font-semibold text-label">
                Projekt {project.auftragsnummer}
              </p>
            ) : null}
            {meta.length ? (
              <p className="max-w-sm text-center text-xs text-label-secondary">
                {meta.join(" · ")}
              </p>
            ) : null}
            <p className="text-center text-xs text-label-tertiary">
              QR-Code mit dem Smartphone scannen
            </p>
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all text-center text-xs text-accent"
            >
              {data.url}
            </a>
            <Button type="button" size="sm" variant="secondary" onClick={() => void copyLink()}>
              {copied ? "Link kopiert" : "Link kopieren"}
            </Button>
          </div>
        ) : null}

        {data ? (
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wide text-label-secondary">
              Aushang drucken
            </h4>
            <div className="eh-po-print-grid">
              <NativeSelectField
                label="Standort"
                selectSize="sm"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
              >
                {locations.length === 0 ? (
                  <option value="">— Standort auswählen —</option>
                ) : null}
                {locations.map((loc, idx) => {
                  const id = loc.id ?? loc.key ?? loc.value ?? idx;
                  const label = loc.name || loc.label || String(id);
                  return (
                    <option key={String(id)} value={String(id)}>
                      {label}
                    </option>
                  );
                })}
              </NativeSelectField>
              <TextInput
                label="Kopien"
                type="number"
                min={1}
                inputClassName="!w-full"
                className="mb-0"
                value={copies}
                onChange={(e) => setCopies(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!!printBusy || !locationId}
                onClick={() => void printPoster("A4")}
              >
                {printBusy === "A4" ? "Druckt …" : "DIN A4 drucken"}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!!printBusy || !locationId}
                onClick={() => void printPoster("A3")}
              >
                {printBusy === "A3" ? "Druckt …" : "DIN A3 drucken"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={!!printBusy}
                onClick={() => void openPdf("A4")}
              >
                PDF A4
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={!!printBusy}
                onClick={() => void openPdf("A3")}
              >
                PDF A3
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
